import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { LargeTitle, Surface, Pill, ListGroup, ListItem, AButton, Segmented, Skeleton } from "@/components/ios";
import { ChevronLeft, ShieldCheck, User, Home, FileText, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export default function PropertyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [open, setOpen] = useState(false);
  const [rtype, setRtype] = useState("routine");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/properties/${id}`).then(({ data }) => setP(data));
  useEffect(() => { load(); }, [id]);

  const startInspection = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/inspections", { property_id: id, report_type: rtype, mode: "checklist" });
      toast.success("Inspection started");
      nav(`/inspections/${data.id}/capture`);
    } catch (e) { toast.error("Could not start inspection"); }
    finally { setBusy(false); }
  };

  if (!p) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-48" /></div>;

  const t = p.tenancy?.tenants?.[0];
  const o = p.tenancy?.owners?.[0];

  return (
    <div>
      <button onClick={() => nav("/properties")} className="flex items-center gap-1 text-sm text-secondary mb-4"><ChevronLeft size={16} /> Properties</button>
      <div className="rounded-2xl overflow-hidden ios-shadow mb-6">
        <div className="h-48 bg-cover bg-center relative" style={{ backgroundImage: `url(${p.image})` }}>
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.6), transparent)" }} />
          <div className="absolute bottom-4 left-5 text-white">
            <div className="text-2xl font-bold">{p.address}</div>
            <div className="text-white/80">{p.suburb}, {p.state} {p.postcode}</div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <AButton testid="start-inspection-btn" onClick={() => setOpen(true)}><Plus size={16} className="inline mr-1" /> New inspection</AButton>
        <AButton variant="text" onClick={() => nav("/notices")}>Send entry notice</AButton>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <ListGroup header="Tenancy">
          <ListItem right={<span className="text-sm text-secondary">{t?.name || "Vacant"}</span>}><span className="flex items-center gap-2" style={{ color: "var(--text)" }}><User size={15} className="text-secondary" /> Tenant</span></ListItem>
          <ListItem right={<span className="text-sm text-secondary">{o?.name}</span>}><span className="flex items-center gap-2" style={{ color: "var(--text)" }}><Home size={15} className="text-secondary" /> Owner</span></ListItem>
          <ListItem right={<span className="text-sm text-secondary">${p.tenancy?.rent_pw}/wk</span>}><span style={{ color: "var(--text)" }}>Rent</span></ListItem>
          <ListItem right={<Pill tone={new Date(p.next_inspection_due) < new Date() ? "danger" : "warning"}>{new Date(p.next_inspection_due).toLocaleDateString("en-AU")}</Pill>}><span style={{ color: "var(--text)" }}>Next inspection</span></ListItem>
        </ListGroup>

        <ListGroup header="Compliance">
          {(p.compliance || []).map((c) => {
            const exp = new Date(c.expires_at);
            const status = exp < new Date() ? "danger" : (exp < new Date(Date.now() + 30 * 864e5) ? "warning" : "success");
            return (
              <ListItem key={c.id} right={<Pill tone={status}>{exp.toLocaleDateString("en-AU")}</Pill>}>
                <span className="flex items-center gap-2" style={{ color: "var(--text)" }}><ShieldCheck size={15} className="text-secondary" /> {c.name}</span>
              </ListItem>
            );
          })}
        </ListGroup>

        <ListGroup header="Inspection history">
          {(p.inspections || []).length === 0 ? (
            <ListItem><span className="text-sm text-secondary">No inspections yet</span></ListItem>
          ) : p.inspections.map((ins) => (
            <ListItem key={ins.id} testid={`insp-hist-${ins.id}`} onClick={() => nav(`/inspections/${ins.id}/capture`)}
              right={<Pill tone="accent">{ins.status.replace(/_/g, " ")}</Pill>}>
              <span className="flex items-center gap-2 capitalize" style={{ color: "var(--text)" }}><FileText size={15} className="text-secondary" /> {ins.report_type} · {new Date(ins.created_at).toLocaleDateString("en-AU")}</span>
            </ListItem>
          ))}
        </ListGroup>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <DialogHeader><DialogTitle style={{ color: "var(--text)" }}>New inspection</DialogTitle></DialogHeader>
          <p className="text-sm text-secondary">Renders the {p.state} condition report schema as a live checklist.</p>
          <div className="my-4">
            <Segmented testid="report-type-seg" value={rtype} onChange={setRtype} options={[
              { value: "entry", label: "Entry" }, { value: "routine", label: "Routine" }, { value: "exit", label: "Exit" },
            ]} />
          </div>
          <AButton testid="confirm-start-inspection" onClick={startInspection} disabled={busy} className="w-full">
            {busy ? "Starting…" : "Start capture"}
          </AButton>
        </DialogContent>
      </Dialog>
    </div>
  );
}
